import { z } from 'zod';

import {
  type AppealStyle,
  type CandidateTrace,
  type CompositionFieldSpec,
  type CtaEmphasisLevel,
  type FamilyRegistry,
  type GenerationSourceInventory,
  type HeroPattern,
  type PageCompositionJson,
  type PageContext,
  type SectionSystemFieldValues,
  type SectionTemplateFieldSpec,
  type SectionTemplateRegistry,
  type SelectedThemeSnapshot,
  type SelectionTrace,
  type SkeletonRegistry,
  type SourceRecord,
  type SystemFieldValue,
  type ThemeEmphasisChannel,
  type ThemeRegistry,
  type VisualTone,
  type WritingStyle,
  buildSelectedThemeSnapshot,
  compositionSectionSchema,
  generationClassificationSchema,
  generationSourceInventorySchema,
  pageCompositionJsonSchema,
} from './schema';
import {
  FAMILY_PRESETS,
  FAMILY_REGISTRIES,
  getSectionTemplateRegistry,
  listSectionTemplatesByType,
  listSkeletonsForFamily,
  THEME_REGISTRIES,
} from './registry';

export const normalizedHintsSchema = z.object({
  serviceSummary: z.string().default(''),
  audience: z.string().default(''),
  keyStrengths: z.array(z.string()).default([]),
}).optional();

export const buildPageCompositionInputSchema = z.object({
  siteName: z.string().min(1),
  companyName: z.string().default(''),
  classification: generationClassificationSchema,
  sourceInventory: generationSourceInventorySchema,
  normalizedHints: normalizedHintsSchema,
});

export type BuildPageCompositionInput = z.infer<
  typeof buildPageCompositionInputSchema
>;

type ScoredCandidate<T> = {
  candidate: T;
  trace: CandidateTrace;
};

type TemplateSourceState = {
  requiredSourceIds: string[];
  optionalSourceIds: string[];
  missingRequiredKinds: string[];
};

type EvaluatedTemplateCandidate = {
  template: SectionTemplateRegistry;
  trace: CandidateTrace;
  sourceState: TemplateSourceState;
};

export type SectionRuntimeState = {
  generationStatus: PageCompositionJson['pages'][number]['sections'][number]['generationStatus'];
  sourceBindings: PageCompositionJson['pages'][number]['sections'][number]['sourceBindings'];
  sourcePolicy: PageCompositionJson['pages'][number]['sections'][number]['sourcePolicy'];
  systemFieldValues: SectionSystemFieldValues;
};

const CTA_EMPHASIS_ORDER: CtaEmphasisLevel[] = ['soft', 'normal', 'strong'];

const SECTION_TYPE_APPEAL_FIT: Record<AppealStyle, string[]> = {
  problem_solution: ['problem', 'solution', 'services', 'features', 'feature_list'],
  benefit_future: ['benefit', 'review', 'pricing', 'offer', 'cta'],
  feature_understanding: ['feature_list', 'features', 'comparison', 'use_cases'],
  trust_relief: ['proof', 'faq', 'reasons', 'review'],
  story_empathy: ['review', 'message', 'people', 'benefit'],
  offer_push: ['offer', 'pricing', 'cta'],
  people_culture: ['people', 'work_style', 'message', 'review'],
  authority_expert: ['proof', 'overview', 'speaker', 'instructor', 'comparison'],
};

const APPEAL_THEME_PREFERENCES: Record<AppealStyle, ThemeRegistry['visualTone'][]> = {
  problem_solution: ['corporate', 'trust', 'modern'],
  benefit_future: ['premium', 'bright_clean', 'friendly'],
  feature_understanding: ['modern', 'corporate', 'trust'],
  trust_relief: ['trust', 'corporate', 'natural_warm'],
  story_empathy: ['natural_warm', 'friendly', 'premium'],
  offer_push: ['bright_clean', 'premium', 'friendly'],
  people_culture: ['natural_warm', 'friendly', 'trust'],
  authority_expert: ['trust', 'corporate', 'modern'],
};

const THEME_FALLBACK_PRIORITY: Record<ThemeRegistry['visualTone'], number> = {
  trust: 0,
  corporate: 1,
  friendly: 2,
  natural_warm: 3,
  modern: 4,
  premium: 5,
  bright_clean: 6,
};

function compareScoredCandidates<T>(left: ScoredCandidate<T>, right: ScoredCandidate<T>) {
  if (right.trace.score !== left.trace.score) {
    return right.trace.score - left.trace.score;
  }

  return right.candidate instanceof Object &&
    'priority' in (right.candidate as Record<string, unknown>) &&
    left.candidate instanceof Object &&
    'priority' in (left.candidate as Record<string, unknown>)
    ? Number((right.candidate as { priority?: number }).priority ?? 0)
      - Number((left.candidate as { priority?: number }).priority ?? 0)
    : 0;
}

function unique<T>(values: T[]): T[] {
  return [...new Set(values)];
}

function sanitizeValue(value?: string) {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function findAvailableSources(
  sourceInventory: GenerationSourceInventory,
  kinds: string[],
  verifiedOnly: boolean,
) {
  return sourceInventory.records.filter((record) => {
    if (!kinds.includes(record.kind)) {
      return false;
    }

    if (record.availability !== 'available') {
      return false;
    }

    if (verifiedOnly) {
      return record.verification === 'verified';
    }

    return true;
  });
}

function sourceKindAvailable(
  sourceInventory: GenerationSourceInventory,
  kind: string,
): boolean {
  return findAvailableSources(sourceInventory, [kind], false).length > 0;
}

function hasVerifiedSourceForKinds(
  sourceInventory: GenerationSourceInventory,
  kinds: string[],
): boolean {
  return findAvailableSources(sourceInventory, kinds, true).length > 0;
}

function findSourceRecordsByIds(
  sourceInventory: GenerationSourceInventory,
  ids: string[],
) {
  const idSet = new Set(ids);
  return sourceInventory.records.filter((record) => idSet.has(record.id));
}

function findFieldSourceRecords(
  sourceInventory: GenerationSourceInventory,
  field: SectionTemplateFieldSpec,
  sourceState: TemplateSourceState,
) {
  if (field.sourceKinds.length === 0) {
    return [];
  }

  const boundRecords = findSourceRecordsByIds(sourceInventory, [
    ...sourceState.requiredSourceIds,
    ...sourceState.optionalSourceIds,
  ]).filter((record) => field.sourceKinds.includes(record.kind));

  if (boundRecords.length > 0) {
    return boundRecords;
  }

  const verifiedRecords = findAvailableSources(
    sourceInventory,
    field.sourceKinds,
    true,
  );

  if (verifiedRecords.length > 0) {
    return verifiedRecords;
  }

  return findAvailableSources(sourceInventory, field.sourceKinds, false);
}

function findUsableSystemFieldSourceRecords(
  sourceInventory: GenerationSourceInventory,
  field: SectionTemplateFieldSpec,
) {
  if (field.allowAiGeneration || field.sourceKinds.length === 0) {
    return [];
  }

  const records = findAvailableSources(sourceInventory, field.sourceKinds, false);
  if (records.length === 0) {
    return [];
  }

  return resolveSystemFieldValue(field, records) !== undefined ? records : [];
}

function pickStructuredString(
  record: SourceRecord,
  keys: string[],
) {
  const data = record.structuredData;
  if (data) {
    for (const key of keys) {
      const value = data[key as keyof typeof data];
      if (typeof value === 'string') {
        const sanitized = sanitizeValue(value);
        if (sanitized) {
          return sanitized;
        }
      }
    }
  }

  return sanitizeValue(record.label);
}

function mapMetricItems(records: SourceRecord[]) {
  const items = records.flatMap((record) => {
    const structuredItems = record.structuredData?.items ?? [];
    if (structuredItems.length > 0) {
      return structuredItems.map((item) => {
        const value = sanitizeValue(item.value);
        const label = sanitizeValue(item.label ?? item.title ?? item.body);
        if (!value || !label) {
          return null;
        }

        return { value, label };
      });
    }

    const value = pickStructuredString(record, ['value']);
    const label = pickStructuredString(record, ['label', 'title', 'body']);
    if (!value || !label) {
      return [];
    }

    return [{ value, label }];
  });

  return items.filter(
    (
      item,
    ): item is {
      label: string;
      value: string;
    } => Boolean(item),
  );
}

function mapPersonItems(records: SourceRecord[]) {
  const items = records.flatMap((record) => {
    const structuredItems = record.structuredData?.items ?? [];
    if (structuredItems.length > 0) {
      return structuredItems.map((item) => {
        const name = sanitizeValue(item.name);
        const comment = sanitizeValue(item.comment ?? item.body);
        const role = sanitizeValue(item.role);
        const image = sanitizeValue(item.image);

        if (!name || !comment) {
          return null;
        }

        return {
          ...(image ? { image } : {}),
          ...(role ? { role } : {}),
          comment,
          name,
        };
      });
    }

    const name = pickStructuredString(record, ['name', 'title']);
    const comment = pickStructuredString(record, ['comment', 'body']);
    const role = pickStructuredString(record, ['role']);
    if (!name || !comment) {
      return [];
    }

    return [{ ...(role ? { role } : {}), comment, name }];
  });

  return items.filter(
    (
      item,
    ): item is {
      comment: string;
      image?: string;
      name: string;
      role?: string;
    } => Boolean(item),
  );
}

function mapPriceItems(records: SourceRecord[]) {
  const items = records.flatMap((record) => {
    const structuredItems = record.structuredData?.items ?? [];
    if (structuredItems.length > 0) {
      return structuredItems.map((item) => {
        const title = sanitizeValue(item.title ?? item.name);
        const price = sanitizeValue(item.price ?? item.value);
        const body = sanitizeValue(item.body ?? item.comment);
        if (!title || !price) {
          return null;
        }

        return {
          ...(body ? { body } : {}),
          price,
          title,
        };
      });
    }

    const title = pickStructuredString(record, ['title', 'name']);
    const price = pickStructuredString(record, ['price', 'value']);
    const body = pickStructuredString(record, ['body', 'comment']);
    if (!title || !price) {
      return [];
    }

    return [{ ...(body ? { body } : {}), price, title }];
  });

  return items.filter(
    (
      item,
    ): item is {
      body?: string;
      price: string;
      title: string;
    } => Boolean(item),
  );
}

function mapGenericItems(records: SourceRecord[]) {
  const items = records.flatMap((record) => {
    const structuredItems = record.structuredData?.items ?? [];
    if (structuredItems.length > 0) {
      return structuredItems.map((item) => {
        const title = sanitizeValue(item.title ?? item.name ?? item.label);
        const body = sanitizeValue(item.body ?? item.comment ?? item.value);
        if (!title || !body) {
          return null;
        }

        return { body, title };
      });
    }

    const title = pickStructuredString(record, ['title', 'name']);
    const body = pickStructuredString(record, ['body', 'comment', 'value']);
    if (!title || !body) {
      return [];
    }

    return [{ body, title }];
  });

  return items.filter(
    (
      item,
    ): item is {
      body: string;
      title: string;
    } => Boolean(item),
  );
}

function resolveSystemFieldValue(
  field: SectionTemplateFieldSpec,
  records: SourceRecord[],
): SystemFieldValue | undefined {
  if (records.length === 0) {
    return undefined;
  }

  switch (field.kind) {
    case 'metric_list': {
      const items = mapMetricItems(records).slice(0, field.maxItems);
      return items.length > 0 ? items : undefined;
    }
    case 'person_list': {
      const items = mapPersonItems(records).slice(0, field.maxItems);
      return items.length > 0 ? items : undefined;
    }
    case 'price_list': {
      const items = mapPriceItems(records).slice(0, field.maxItems);
      return items.length > 0 ? items : undefined;
    }
    case 'item_list': {
      const items = mapGenericItems(records).slice(0, field.maxItems);
      return items.length > 0 ? items : undefined;
    }
    case 'text':
    case 'textarea': {
      const fieldNameKeys: Record<string, string[]> = {
        accessNote: ['accessNote', 'body', 'comment'],
        address: ['address', 'body'],
        body: ['body', 'comment', 'accessNote'],
        heading: ['title', 'name', 'value'],
      };
      const keys = fieldNameKeys[field.name] ?? [
        field.name,
        'body',
        'title',
        'comment',
        'value',
        'name',
      ];

      for (const record of records) {
        const value = pickStructuredString(record, keys);
        if (value) {
          return value;
        }
      }

      return undefined;
    }
    default:
      return undefined;
  }
}

function buildSystemFieldValues(
  template: SectionTemplateRegistry,
  sourceState: TemplateSourceState,
  sourceInventory: GenerationSourceInventory,
): SectionSystemFieldValues {
  const values: SectionSystemFieldValues = {};
  const fields = [...template.requiredFields, ...template.optionalFields];

  for (const field of fields) {
    if (field.allowAiGeneration) {
      continue;
    }

    const records = findFieldSourceRecords(sourceInventory, field, sourceState);
    const resolvedValue = resolveSystemFieldValue(field, records);

    if (resolvedValue !== undefined) {
      values[field.name] = resolvedValue;
    }
  }

  return values;
}

function inferVisualGenerationPolicy(
  field: SectionTemplateFieldSpec,
  preferredAssetKinds: SectionTemplateRegistry['assetRequirements'][number]['assetKind'][],
): PageCompositionJson['pages'][number]['sections'][number]['visualSlots'][number]['generationPolicy'] {
  const sourceKinds = unique([
    ...field.sourceKinds,
    ...preferredAssetKinds,
  ]);

  if (sourceKinds.includes('ui_capture')) {
    return 'diagram_only';
  }

  if (
    sourceKinds.includes('person_photo') ||
    sourceKinds.includes('team_photo') ||
    sourceKinds.includes('logo')
  ) {
    return 'reuse_only';
  }

  if (
    sourceKinds.includes('location_photo') ||
    sourceKinds.includes('service_photo')
  ) {
    return 'ai_allowed';
  }

  return 'none';
}

function buildSectionVisualSlots(
  template: SectionTemplateRegistry,
): PageCompositionJson['pages'][number]['sections'][number]['visualSlots'] {
  const imageFields = [...template.requiredFields, ...template.optionalFields].filter(
    (field) => field.kind === 'image',
  );

  return imageFields.map((field) => {
    const matchingRequirements = template.assetRequirements.filter(
      (asset) => asset.slotName === field.name,
    );
    const preferredAssetKinds = unique(
      matchingRequirements.map((asset) => asset.assetKind),
    );
    const sourceKinds = unique([
      ...field.sourceKinds,
      ...preferredAssetKinds,
    ]);
    const primaryRequirement = matchingRequirements[0];

    return {
      slotName: field.name,
      required: field.required,
      purpose: primaryRequirement?.purpose ?? field.description,
      sourceKinds,
      preferredAssetKinds,
      generationPolicy:
        primaryRequirement?.generationPolicy ??
        inferVisualGenerationPolicy(field, preferredAssetKinds),
      promptHints: unique(
        matchingRequirements.flatMap((asset) => asset.promptHints),
      ),
      forbiddenElements: unique(
        matchingRequirements.flatMap((asset) => asset.forbiddenElements),
      ),
    };
  });
}

export function buildSectionRuntimeState(
  template: SectionTemplateRegistry,
  sourceInventory: GenerationSourceInventory,
): SectionRuntimeState {
  const sourceState = buildTemplateSourceState(template, sourceInventory);
  const requiredVerifiedFields = template.requiredFields
    .filter((field) => field.requiresVerifiedSource)
    .map((field) => field.name);
  const optionalVerifiedFields = template.optionalFields
    .filter((field) => field.requiresVerifiedSource)
    .map((field) => field.name);

  const generationStatus =
    sourceState.missingRequiredKinds.length > 0
      ? requiredVerifiedFields.length > 0 ||
          template.assetRequirements.some((asset) => asset.required)
        ? 'needs_verified_source'
        : 'needs_user_input'
      : 'ready';

  return {
    generationStatus,
    sourceBindings: sourceState,
    sourcePolicy: {
      allowAiGeneratedFields: unique(
        [...template.requiredFields, ...template.optionalFields]
          .filter((field) => field.allowAiGeneration)
          .map((field) => field.name),
      ),
      requiredVerifiedFields,
      optionalVerifiedFields,
      forbiddenFabricationFields: unique([
        ...requiredVerifiedFields,
        ...optionalVerifiedFields,
      ]),
      sourceNotes:
        requiredVerifiedFields.length > 0
          ? [`verified source required: ${requiredVerifiedFields.join(', ')}`]
          : [],
    },
    systemFieldValues: buildSystemFieldValues(
      template,
      sourceState,
      sourceInventory,
    ),
  };
}

function desiredDominantChannel(appealStyle: AppealStyle): ThemeEmphasisChannel {
  switch (appealStyle) {
    case 'offer_push':
      return 'cta';
    case 'trust_relief':
    case 'authority_expert':
      return 'proof';
    case 'benefit_future':
    case 'story_empathy':
    case 'people_culture':
      return 'visual';
    case 'problem_solution':
    case 'feature_understanding':
    default:
      return 'copy';
  }
}

function desiredCtaIntensity(
  objective: BuildPageCompositionInput['classification']['objective']['value'],
): CtaEmphasisLevel {
  switch (objective) {
    case 'purchase':
    case 'reservation':
      return 'strong';
    case 'application':
      return 'normal';
    default:
      return 'normal';
  }
}

function capCtaIntensity(
  desired: CtaEmphasisLevel,
  maxAllowed: CtaEmphasisLevel,
): CtaEmphasisLevel {
  return CTA_EMPHASIS_ORDER[
    Math.min(
      CTA_EMPHASIS_ORDER.indexOf(desired),
      CTA_EMPHASIS_ORDER.indexOf(maxAllowed),
    )
  ];
}

function densityOptionalLimit(
  skeleton: SkeletonRegistry,
  density: BuildPageCompositionInput['classification']['informationDensity']['value'],
): number {
  switch (density) {
    case 'light':
      return skeleton.densityPolicy.lightMaxOptionalSections;
    case 'rich':
      return skeleton.densityPolicy.richMaxOptionalSections;
    case 'standard':
    default:
      return skeleton.densityPolicy.standardMaxOptionalSections;
  }
}

function scoreFamilyCandidate(
  family: FamilyRegistry,
  input: BuildPageCompositionInput,
): CandidateTrace {
  const objective = input.classification.objective.value;
  const industry = input.classification.industry.value;
  const appealStyle = input.classification.appealStyle.primary.value;
  const droppedReasons: string[] = [];
  const notes: string[] = [];
  let score = family.priority;
  const applicationFallbackFamilyByIndustry: Partial<
    Record<typeof industry, FamilyRegistry['id']>
  > = {
    b2b_service: 'btob_inquiry',
    saas_ai: 'saas_ai_inquiry',
    regional_service: 'regional_service_inquiry',
    store_reservation: 'store_reservation',
    d2c_product: 'd2c_purchase',
    recruitment: 'recruitment_entry',
  };
  const applicationFallbackFamily =
    objective === 'application'
      ? applicationFallbackFamilyByIndustry[industry]
      : undefined;
  const objectiveCompatible =
    family.objectiveMatches.includes(objective) ||
    applicationFallbackFamily === family.id;

  if (
    family.id === 'recruitment_entry' &&
    objective === 'application' &&
    industry !== 'recruitment'
  ) {
    droppedReasons.push('recruitment application requires recruitment industry');
  }

  if (!objectiveCompatible) {
    droppedReasons.push(`objective ${objective} is not supported`);
  } else {
    score += 40;
    notes.push(
      applicationFallbackFamily === family.id
        ? 'application converted to industry conversion family'
        : 'objective match',
    );
  }

  if (family.industryMatches.includes(industry)) {
    score += 30;
    notes.push('industry match');
  } else if (industry === 'general_other') {
    score += 8;
    notes.push('general fallback');
  }

  if (family.preferredAppealStyles.includes(appealStyle)) {
    score += 18;
    notes.push('preferred appeal match');
  }

  if (family.avoidedAppealStyles.includes(appealStyle)) {
    score -= 12;
    notes.push('avoided appeal penalty');
  }

  if (appealStyle === 'people_culture' && family.id === 'recruitment_entry') {
    score += 24;
    notes.push('people_culture recruitment boost');
  }

  if (
    appealStyle === 'feature_understanding' &&
    family.id === 'saas_ai_inquiry'
  ) {
    score += 18;
    notes.push('feature understanding saas boost');
  }

  if (
    appealStyle === 'offer_push' &&
    objective === 'purchase' &&
    family.id === 'd2c_purchase'
  ) {
    score += 20;
    notes.push('offer push purchase boost');
  }

  if (objective === 'reservation') {
    if (family.id === 'store_reservation') {
      score += 16;
      if (
        input.classification.interactionLevel.value === 'mobile_conversion_focused'
      ) {
        score += 8;
        notes.push('mobile reservation fit');
      }
      if (input.sourceInventory.summary.hasVerifiedAccess) {
        score += 4;
        notes.push('access source fit');
      }
    }

    if (family.id === 'regional_service_inquiry') {
      score += 10;
      notes.push('reservation fallback family');
    }
  }

  if (
    objective === 'application' &&
    industry === 'general_other' &&
    family.id === 'seminar_application' &&
    appealStyle === 'authority_expert'
  ) {
    score += 8;
  }

  if (
    objective === 'application' &&
    industry === 'general_other' &&
    family.id === 'education_application' &&
    appealStyle === 'trust_relief'
  ) {
    score += 8;
  }

  return {
    candidateId: family.id,
    score,
    droppedReasons,
    notes,
    selected: false,
    fallbackApplied: false,
  };
}

function selectFamily(input: BuildPageCompositionInput) {
  const candidates: ScoredCandidate<FamilyRegistry>[] = FAMILY_REGISTRIES.map(
    (family) => ({
      candidate: family,
      trace: scoreFamilyCandidate(family, input),
    }),
  );

  const selectable = candidates.filter(
    (candidate) => candidate.trace.droppedReasons.length === 0,
  );
  const selected =
    selectable.sort(compareScoredCandidates)[0] ??
    candidates.sort(compareScoredCandidates)[0];

  selected.trace.selected = true;

  return {
    selected: selected.candidate,
    trace: candidates.map((candidate) =>
      candidate.candidate.id === selected.candidate.id
        ? selected.trace
        : candidate.trace,
    ),
  };
}

function scoreSkeletonCandidate(
  skeleton: SkeletonRegistry,
  family: FamilyRegistry,
  input: BuildPageCompositionInput,
): CandidateTrace {
  const density = input.classification.informationDensity.value;
  const devicePriority = input.classification.devicePriority.value;
  const notes: string[] = [];
  let score = skeleton.priority + 40;

  score += skeleton.requiredSections.filter((sectionType) =>
    family.requiredSectionTypes.includes(sectionType),
  ).length;
  notes.push('family skeleton match');

  if (density === 'light' && skeleton.densityPolicy.lightMaxOptionalSections <= 2) {
    score += 10;
    notes.push('light density fit');
  }

  if (
    density === 'rich' &&
    skeleton.densityPolicy.richMaxOptionalSections >= 3
  ) {
    score += 10;
    notes.push('rich density fit');
  }

  if (
    devicePriority === 'mobile_first' &&
    skeleton.ctaPolicy.allowStickyMobileCta
  ) {
    score += 8;
    notes.push('mobile CTA fit');
  }

  if (
    devicePriority === 'desktop_first' &&
    skeleton.sectionOrder.includes('comparison')
  ) {
    score += 6;
    notes.push('desktop comparison fit');
  }

  return {
    candidateId: skeleton.id,
    score,
    droppedReasons: [],
    notes,
    selected: false,
    fallbackApplied: false,
  };
}

function selectSkeleton(
  family: FamilyRegistry,
  input: BuildPageCompositionInput,
) {
  const candidates = listSkeletonsForFamily(family.id).map((skeleton) => ({
    candidate: skeleton,
    trace: scoreSkeletonCandidate(skeleton, family, input),
  }));

  const preset = FAMILY_PRESETS[family.id];
  const presetMatch = preset
    ? candidates.find((c) => c.candidate.id === preset.skeletonId)
    : undefined;
  const selected = presetMatch ?? candidates.sort(compareScoredCandidates)[0];
  selected.trace.selected = true;
  if (presetMatch) {
    selected.trace.notes.push(`family preset pinned skeleton ${preset.skeletonId}`);
  }

  return {
    selected: selected.candidate,
    trace: candidates.map((candidate) =>
      candidate.candidate.id === selected.candidate.id
        ? selected.trace
        : candidate.trace,
    ),
  };
}

function scoreThemeCandidate(
  theme: ThemeRegistry,
  family: FamilyRegistry,
  input: BuildPageCompositionInput,
): CandidateTrace {
  const desiredTone = input.classification.visualTone.value;
  const desiredDensity = input.classification.informationDensity.value;
  const desiredDevice = input.classification.devicePriority.value;
  const desiredMaterial = input.classification.materialDependency.value;
  const desiredAppeal = input.classification.appealStyle.primary.value;
  const desiredDominance = desiredDominantChannel(desiredAppeal);
  const notes: string[] = [];
  const droppedReasons: string[] = [];
  let score = theme.priority;

  if (theme.forbiddenFamilies.includes(family.id)) {
    droppedReasons.push(`family ${family.id} is forbidden by theme`);
  }

  if (theme.visualTone === desiredTone) {
    score += 40;
    notes.push('visual tone match');
  } else if (family.defaultThemeIds.includes(theme.id)) {
    score += 16;
    notes.push('family default theme fallback');
  } else if (THEME_FALLBACK_PRIORITY[theme.visualTone] <= 2) {
    score += 6;
    notes.push('safe fallback tone');
  }

  if (theme.compatibleFamilies.includes(family.id)) {
    score += 30;
    notes.push('family compatibility');
  }

  if (!theme.discouragedFamilies.includes(family.id)) {
    score += 10;
  } else {
    score -= 12;
    notes.push('discouraged family penalty');
  }

  if (theme.informationDensityProfile.preferredDensities.includes(desiredDensity)) {
    score += 10;
    notes.push('density fit');
  }

  if (
    (desiredDevice === 'mobile_first' &&
      theme.informationDensityProfile.contentWidth !== 'wide') ||
    (desiredDevice === 'desktop_first' &&
      theme.informationDensityProfile.contentWidth === 'wide') ||
    desiredDevice === 'balanced'
  ) {
    score += 10;
    notes.push('device fit');
  }

  switch (desiredMaterial) {
    case 'ui_capture_required':
      if (theme.visualAssetPolicy.uiCaptureUsage === 'recommended') {
        score += 10;
      } else if (theme.visualAssetPolicy.uiCaptureUsage === 'optional') {
        score += 4;
      } else {
        score -= 10;
      }
      break;
    case 'photo_required':
    case 'person_photo_required':
      if (
        theme.visualAssetPolicy.photoUsage === 'recommended' ||
        theme.visualAssetPolicy.photoUsage === 'hero_dominant'
      ) {
        score += 10;
      } else if (theme.visualAssetPolicy.photoUsage === 'optional') {
        score += 3;
      } else {
        score -= 10;
      }
      break;
    case 'text_first':
      if (theme.visualAssetPolicy.photoUsage === 'avoid') {
        score += 10;
      } else if (theme.visualAssetPolicy.photoUsage === 'optional') {
        score += 6;
      }
      break;
    default:
      score += 4;
      break;
  }

  if (APPEAL_THEME_PREFERENCES[desiredAppeal].includes(theme.visualTone)) {
    score += 10;
    notes.push('appeal fit');
  }

  if (theme.emphasisPriority[0] === desiredDominance) {
    score += 10;
    notes.push('dominant channel exact fit');
  } else if (theme.emphasisPriority.slice(0, 2).includes(desiredDominance)) {
    score += 5;
  }

  if (
    input.sourceInventory.summary.hasUiCapture &&
    theme.visualAssetPolicy.uiCaptureUsage === 'recommended'
  ) {
    score += 4;
  }

  if (
    input.sourceInventory.summary.hasServicePhoto &&
    theme.visualAssetPolicy.photoUsage !== 'avoid'
  ) {
    score += 4;
  }

  if (
    input.sourceInventory.summary.overallMaterialQuality === 'low' &&
    theme.materialSensitivity === 'high'
  ) {
    score -= 25;
    notes.push('material sensitivity penalty');
  } else if (
    input.sourceInventory.summary.overallMaterialQuality === 'low' &&
    theme.materialSensitivity === 'medium'
  ) {
    score -= 10;
  }

  return {
    candidateId: theme.id,
    score,
    droppedReasons,
    notes,
    selected: false,
    fallbackApplied: false,
  };
}

function selectTheme(
  family: FamilyRegistry,
  input: BuildPageCompositionInput,
) {
  const candidates: ScoredCandidate<ThemeRegistry>[] = THEME_REGISTRIES.map(
    (theme) => ({
      candidate: theme,
      trace: scoreThemeCandidate(theme, family, input),
    }),
  );

  const preset = FAMILY_PRESETS[family.id];
  const presetMatch = preset
    ? candidates.find((c) => c.candidate.id === preset.themeId)
    : undefined;

  if (presetMatch) {
    presetMatch.trace.selected = true;
    presetMatch.trace.notes.push(`family preset pinned theme ${preset.themeId}`);
    return {
      selected: presetMatch.candidate,
      trace: candidates.map((candidate) =>
        candidate.candidate.id === presetMatch.candidate.id
          ? presetMatch.trace
          : candidate.trace,
      ),
    };
  }

  const selectable = candidates
    .filter((candidate) => candidate.trace.droppedReasons.length === 0)
    .sort((left, right) => {
      if (right.trace.score !== left.trace.score) {
        return right.trace.score - left.trace.score;
      }

      const toneDelta =
        THEME_FALLBACK_PRIORITY[left.candidate.visualTone]
        - THEME_FALLBACK_PRIORITY[right.candidate.visualTone];

      if (toneDelta !== 0) {
        return toneDelta;
      }

      return right.candidate.priority - left.candidate.priority;
    });

  const selected = selectable[0] ?? candidates.sort(compareScoredCandidates)[0];
  selected.trace.selected = true;

  return {
    selected: selected.candidate,
    trace: candidates.map((candidate) =>
      candidate.candidate.id === selected.candidate.id
        ? selected.trace
        : candidate.trace,
    ),
  };
}

function scoreOptionalSection(
  sectionType: string,
  family: FamilyRegistry,
  theme: ThemeRegistry,
  input: BuildPageCompositionInput,
): number {
  let score = family.recommendedSectionTypes.includes(sectionType as never) ? 20 : 0;
  const appeal = input.classification.appealStyle.primary.value;
  const device = input.classification.devicePriority.value;

  if (SECTION_TYPE_APPEAL_FIT[appeal].includes(sectionType)) {
    score += 14;
  }

  if (family.discouragedSectionTypes.includes(sectionType as never)) {
    score -= 20;
  }

  switch (sectionType) {
    case 'review':
      score += hasVerifiedSourceForKinds(input.sourceInventory, ['review']) ? 12 : -24;
      if (
        family.id === 'btob_inquiry' &&
        input.classification.objective.value === 'document_request' &&
        sourceKindAvailable(input.sourceInventory, 'review')
      ) {
        score += 28;
      }
      break;
    case 'proof':
      score += hasVerifiedSourceForKinds(input.sourceInventory, [
        'proof_metric',
        'proof_case',
        'certification',
      ])
        ? 12
        : -12;
      break;
    case 'pricing':
      score += hasVerifiedSourceForKinds(input.sourceInventory, ['pricing']) ? 10 : 4;
      break;
    case 'access':
      score += hasVerifiedSourceForKinds(input.sourceInventory, ['access'])
        ? 12
        : sourceKindAvailable(input.sourceInventory, 'access')
          ? 6
          : -24;
      break;
    case 'offer':
      score += hasVerifiedSourceForKinds(input.sourceInventory, ['offer']) ? 10 : -18;
      break;
    default:
      break;
  }

  if (
    device === 'mobile_first' &&
    ['review', 'pricing', 'offer', 'faq'].includes(sectionType)
  ) {
    score += 4;
  }

  if (
    device === 'desktop_first' &&
    ['comparison', 'feature_list', 'faq'].includes(sectionType)
  ) {
    score += 4;
  }

  if (family.id === 'regional_service_inquiry') {
    const isTrustNeutralRegionalTheme = ['theme_trust_neutral', 'theme_formal_trust'].includes(
      theme.id,
    );
    const isWarmRegionalTheme = theme.id === 'theme_natural_warm';

    if (isTrustNeutralRegionalTheme) {
      switch (sectionType) {
        case 'review':
          score += 8;
          break;
        case 'flow':
          score += 18;
          break;
        case 'faq':
          score += 8;
          break;
        case 'access':
          score -= 28;
          break;
        case 'message':
          score -= 24;
          break;
        default:
          break;
      }
    }

    if (isWarmRegionalTheme) {
      switch (sectionType) {
        case 'review':
          score += sourceKindAvailable(input.sourceInventory, 'review') ? 24 : -12;
          break;
        case 'access':
          score += sourceKindAvailable(input.sourceInventory, 'access') ? 12 : -18;
          break;
        case 'message':
          score += 16;
          break;
        case 'flow':
          score -= 20;
          break;
        default:
          break;
      }
    }
  }

  return score;
}

function selectSectionTypes(
  family: FamilyRegistry,
  skeleton: SkeletonRegistry,
  theme: ThemeRegistry,
  input: BuildPageCompositionInput,
) {
  const required = new Set<string>(skeleton.requiredSections);
  const baseMaxOptional = densityOptionalLimit(
    skeleton,
    input.classification.informationDensity.value,
  );
  const maxOptional =
    family.id === 'regional_service_inquiry' &&
    ['theme_trust_neutral', 'theme_formal_trust', 'theme_natural_warm'].includes(theme.id) &&
    input.classification.informationDensity.value !== 'light'
      ? Math.max(baseMaxOptional, 3)
      : baseMaxOptional;

  const optionalCandidates = skeleton.optionalSections
    .map((sectionType) => ({
      sectionType,
      score: scoreOptionalSection(sectionType, family, theme, input),
    }))
    .filter((candidate) => candidate.score > 0)
    .sort((left, right) => right.score - left.score)
    .slice(0, maxOptional);

  const selectedOptionals = new Set(
    optionalCandidates.map((candidate) => candidate.sectionType),
  );

  return skeleton.sectionOrder.filter(
    (sectionType) => required.has(sectionType) || selectedOptionals.has(sectionType),
  );
}

function buildTemplateSourceState(
  template: SectionTemplateRegistry,
  sourceInventory: GenerationSourceInventory,
): TemplateSourceState {
  const requiredSourceIds: string[] = [];
  const optionalSourceIds: string[] = [];
  const missingRequiredKinds: string[] = [];

  for (const field of template.requiredFields) {
    if (field.requiresVerifiedSource) {
      const verifiedRecords = findAvailableSources(
        sourceInventory,
        field.sourceKinds,
        true,
      );

      if (verifiedRecords.length > 0) {
        requiredSourceIds.push(...verifiedRecords.map((record) => record.id));
        continue;
      }

      const usableRecords = findUsableSystemFieldSourceRecords(sourceInventory, field);
      if (usableRecords.length > 0) {
        requiredSourceIds.push(...usableRecords.map((record) => record.id));
        continue;
      }

      if (verifiedRecords.length === 0) {
        missingRequiredKinds.push(
          ...(field.sourceKinds.length > 0 ? field.sourceKinds : [field.name]),
        );
      }
    }
  }

  for (const field of template.optionalFields) {
    if (field.requiresVerifiedSource) {
      const records = findAvailableSources(
        sourceInventory,
        field.sourceKinds,
        true,
      );

      if (records.length > 0) {
        optionalSourceIds.push(...records.map((record) => record.id));
        continue;
      }

      optionalSourceIds.push(
        ...findUsableSystemFieldSourceRecords(sourceInventory, field).map(
          (record) => record.id,
        ),
      );
    }
  }

  for (const requirement of template.assetRequirements) {
    const records = findAvailableSources(
      sourceInventory,
      [requirement.assetKind],
      false,
    );

    if (records.length === 0 && requirement.required) {
      missingRequiredKinds.push(requirement.assetKind);
    } else if (records.length > 0) {
      optionalSourceIds.push(...records.map((record) => record.id));
    }
  }

  return {
    requiredSourceIds: unique(requiredSourceIds),
    optionalSourceIds: unique(optionalSourceIds),
    missingRequiredKinds: unique(missingRequiredKinds),
  };
}

function evaluateTemplateCandidate(
  template: SectionTemplateRegistry,
  family: FamilyRegistry,
  theme: ThemeRegistry,
  input: BuildPageCompositionInput,
): EvaluatedTemplateCandidate {
  const droppedReasons: string[] = [];
  const notes: string[] = [];
  let score = template.priority;

  if (!template.supportedFamilies.includes(family.id)) {
    droppedReasons.push(`family ${family.id} is not supported`);
  }

  if (
    !template.supportedDevices.includes(input.classification.devicePriority.value)
  ) {
    droppedReasons.push(
      `device ${input.classification.devicePriority.value} is not supported`,
    );
  }

  if (
    !template.supportedInformationDensity.includes(
      input.classification.informationDensity.value,
    )
  ) {
    droppedReasons.push(
      `density ${input.classification.informationDensity.value} is not supported`,
    );
  }

  if (!template.supportedTones.includes(theme.visualTone)) {
    droppedReasons.push(`theme tone ${theme.visualTone} is not supported`);
  }

  if (
    !template.supportedMaterialDependency.includes(
      input.classification.materialDependency.value,
    )
  ) {
    droppedReasons.push(
      `material dependency ${input.classification.materialDependency.value} is not supported`,
    );
  }

  if (
    !template.supportedInteractions.includes(
      input.classification.interactionLevel.value,
    )
  ) {
    droppedReasons.push(
      `interaction ${input.classification.interactionLevel.value} is not supported`,
    );
  }

  if (theme.forbiddenSectionTemplateIds.includes(template.id)) {
    droppedReasons.push(`theme forbids template ${template.id}`);
  }

  score += template.rankingSignals.familyScore;

  if (
    template.supportedAppealStyles.includes(
      input.classification.appealStyle.primary.value,
    )
  ) {
    score += template.rankingSignals.appealScore;
    notes.push('appeal match');
  }

  if (template.sectionType === 'hero' && template.heroPattern) {
    const desiredPattern = selectHeroPattern(input.classification);
    if (template.heroPattern === desiredPattern) {
      score += 15;
      notes.push(`heroPattern match: ${desiredPattern}`);
    }
  }

  score += template.rankingSignals.deviceScore;
  score += template.rankingSignals.densityScore;
  score += template.rankingSignals.toneScore;
  score += template.rankingSignals.materialScore;

  if (theme.preferredSectionTemplateIds.includes(template.id)) {
    score += 5;
    notes.push('preferred by theme');
  }

  if (theme.discouragedSectionTemplateIds.includes(template.id)) {
    score -= 5;
    notes.push('discouraged by theme');
  }

  if (
    input.classification.devicePriority.value === 'desktop_first' &&
    template.id === 'comparison_table'
  ) {
    score += 3;
  }

  if (
    input.classification.devicePriority.value === 'mobile_first' &&
    template.layoutType === 'carousel'
  ) {
    score += 1;
  }

  if (
    family.id === 'btob_inquiry' &&
    input.classification.objective.value === 'document_request'
  ) {
    switch (template.id) {
      case 'hero_b2b_centered':
        score += 12;
        notes.push('btob document request hero boost');
        break;
      case 'hero_saas_split_visual':
        if (input.classification.materialDependency.value === 'ui_capture_required') {
          score += 14;
          notes.push('btob document request ui hero boost');
        } else {
          score -= 6;
          notes.push('btob document request ui hero penalty');
        }
        break;
      case 'hero_problem_first':
        score -= 3;
        notes.push('btob document request hero de-emphasis');
        break;
      case 'problem_b2b_structured':
        score += 10;
        notes.push('btob structured problem boost');
        break;
      case 'solution_visual_diagram':
        score += 8;
        notes.push('btob solution diagram boost');
        break;
      case 'services_menu_blocks':
        score += 6;
        notes.push('btob service menu boost');
        break;
      case 'proof_metric_cards':
        score += 12;
        notes.push('btob metrics proof boost');
        if (sourceKindAvailable(input.sourceInventory, 'proof_metric')) {
          score += 4;
          notes.push('btob metrics source boost');
        }
        break;
      case 'proof_badges_awards':
        score += 4;
        notes.push('btob badges proof boost');
        if (
          !sourceKindAvailable(input.sourceInventory, 'proof_metric') &&
          (sourceKindAvailable(input.sourceInventory, 'proof_case') ||
            sourceKindAvailable(input.sourceInventory, 'certification'))
        ) {
          score += 6;
          notes.push('btob case proof boost');
        }
        break;
      case 'faq_b2b_reassurance_panels':
        score += 10;
        notes.push('btob faq reassurance boost');
        break;
      case 'faq_two_columns':
        score += 3;
        notes.push('btob faq two-column support');
        break;
      case 'cta_b2b_reassurance_panel':
        score += 18;
        notes.push('btob reassurance cta boost');
        break;
      case 'review_case_reinforcement':
        if (sourceKindAvailable(input.sourceInventory, 'review')) {
          score += 12;
          notes.push('btob case review boost');
        } else {
          score -= 8;
          notes.push('btob case review penalty');
        }
        break;
      case 'review_speech_bubbles':
        score -= 4;
        notes.push('btob speech review de-emphasis');
        break;
      case 'feature_list_compact':
        score += 8;
        notes.push('btob feature list boost');
        break;
      case 'features_visual_explain':
        if (input.classification.materialDependency.value === 'ui_capture_required') {
          score += 7;
          notes.push('btob feature explain boost');
        }
        break;
      case 'comparison_table':
        score += 9;
        notes.push('btob comparison boost');
        break;
      case 'cta_simple_center':
        score += 4;
        notes.push('btob document cta fallback boost');
        break;
      case 'form_standard_contact':
        score += 10;
        notes.push('btob document form boost');
        break;
      default:
        break;
    }
  }

  if (family.id === 'saas_ai_inquiry') {
    switch (template.id) {
      case 'hero_saas_split_visual':
        score += 14;
        notes.push('saas ui hero boost');
        if (input.classification.materialDependency.value === 'ui_capture_required') {
          score += 6;
          notes.push('saas ui capture hero boost');
        }
        if (sourceKindAvailable(input.sourceInventory, 'ui_capture')) {
          score += 4;
          notes.push('saas ui source boost');
        }
        break;
      case 'hero_problem_first':
        score -= 4;
        notes.push('saas hero de-emphasis');
        break;
      case 'feature_list_compact':
        score += 10;
        notes.push('saas feature list boost');
        break;
      case 'features_visual_explain':
        score += 9;
        notes.push('saas feature explain boost');
        if (
          input.classification.materialDependency.value === 'ui_capture_required' ||
          sourceKindAvailable(input.sourceInventory, 'ui_capture')
        ) {
          score += 4;
          notes.push('saas ui explain boost');
        }
        break;
      case 'comparison_table':
        score += 8;
        notes.push('saas comparison boost');
        if (input.classification.devicePriority.value === 'desktop_first') {
          score += 3;
          notes.push('saas desktop comparison boost');
        }
        break;
      case 'proof_metric_cards':
        score += 7;
        notes.push('saas metrics proof boost');
        if (sourceKindAvailable(input.sourceInventory, 'proof_metric')) {
          score += 4;
          notes.push('saas proof source boost');
        }
        break;
      case 'cta_simple_center':
        score += 10;
        notes.push('saas demo cta boost');
        break;
      case 'cta_b2b_reassurance_panel':
        score += 4;
        notes.push('saas panel cta support');
        break;
      case 'form_standard_contact':
        score += 9;
        notes.push('saas inquiry form boost');
        break;
      default:
        break;
    }
  }

  if (
    family.id === 'regional_service_inquiry' &&
    ['theme_trust_neutral', 'theme_formal_trust'].includes(theme.id)
  ) {
    switch (template.id) {
      case 'hero_b2b_centered':
        score += 14;
        notes.push('regional trust hero boost');
        break;
      case 'hero_problem_first':
        score += 5;
        notes.push('regional trust problem-first support');
        break;
      case 'hero_empathy_story':
        score -= 10;
        notes.push('regional trust empathy hero penalty');
        break;
      case 'hero_store_local_visual':
        score -= 8;
        notes.push('regional trust visual hero penalty');
        break;
      case 'services_menu_blocks':
        score += 10;
        notes.push('regional trust services boost');
        break;
      case 'reasons_icon_list':
        score += 12;
        notes.push('regional trust reasons boost');
        break;
      case 'reasons_short_bullets':
        score -= 6;
        notes.push('regional trust short-bullet reasons penalty');
        break;
      case 'proof_metric_cards':
        score += 12;
        notes.push('regional trust metrics proof boost');
        if (sourceKindAvailable(input.sourceInventory, 'proof_metric')) {
          score += 4;
          notes.push('regional trust proof source boost');
        }
        break;
      case 'proof_badges_awards':
        score += 6;
        notes.push('regional trust badges proof support');
        if (
          !sourceKindAvailable(input.sourceInventory, 'proof_metric') &&
          (sourceKindAvailable(input.sourceInventory, 'proof_case') ||
            sourceKindAvailable(input.sourceInventory, 'certification'))
        ) {
          score += 4;
          notes.push('regional trust fallback badges boost');
        }
        break;
      case 'review_speech_bubbles':
        if (sourceKindAvailable(input.sourceInventory, 'review')) {
          score += 8;
          notes.push('regional trust review boost');
        }
        break;
      case 'flow_standard':
        score += 10;
        notes.push('regional trust flow boost');
        break;
      case 'faq_accordion':
        score += 8;
        notes.push('regional trust faq boost');
        break;
      case 'cta_simple_center':
        score += 8;
        notes.push('regional trust cta boost');
        break;
      case 'form_standard_contact':
        score += 8;
        notes.push('regional trust form boost');
        break;
      case 'access_simple_text':
      case 'message_text_block':
        score -= 10;
        notes.push('regional trust secondary section de-emphasis');
        break;
      default:
        break;
    }
  }

  if (family.id === 'regional_service_inquiry' && theme.id === 'theme_natural_warm') {
    switch (template.id) {
      case 'hero_empathy_story':
        score += input.sourceInventory.summary.hasServicePhoto ? 8 : 18;
        notes.push('regional warm empathy hero boost');
        break;
      case 'hero_store_local_visual':
        score += input.sourceInventory.summary.hasServicePhoto ? 16 : 10;
        notes.push('regional warm local-visual hero boost');
        break;
      case 'hero_b2b_centered':
        score -= 12;
        notes.push('regional warm centered hero penalty');
        break;
      case 'services_menu_blocks':
        score += 10;
        notes.push('regional warm services boost');
        break;
      case 'reasons_icon_list':
        score += 10;
        notes.push('regional warm reasons boost');
        break;
      case 'proof_badges_awards':
        score += 12;
        notes.push('regional warm badges proof boost');
        if (sourceKindAvailable(input.sourceInventory, 'certification')) {
          score += 6;
          notes.push('regional warm certification source boost');
        }
        break;
      case 'review_speech_bubbles':
        if (sourceKindAvailable(input.sourceInventory, 'review')) {
          score += 10;
          notes.push('regional warm review boost');
        } else {
          score -= 6;
          notes.push('regional warm review penalty');
        }
        break;
      case 'faq_cards':
        score += 26;
        notes.push('regional warm faq cards boost');
        break;
      case 'faq_accordion':
        score -= 4;
        notes.push('regional warm faq accordion de-emphasis');
        break;
      case 'access_simple_text':
        score += 12;
        notes.push('regional warm access boost');
        break;
      case 'message_text_block':
        score += 12;
        notes.push('regional warm message boost');
        break;
      case 'flow_standard':
        score -= 10;
        notes.push('regional warm flow de-emphasis');
        break;
      case 'cta_simple_center':
        score += 6;
        notes.push('regional warm cta boost');
        break;
      case 'form_standard_contact':
        score += 8;
        notes.push('regional warm form boost');
        break;
      default:
        break;
    }
  }

  const sourceState = buildTemplateSourceState(template, input.sourceInventory);
  if (sourceState.missingRequiredKinds.length === 0) {
    score += 5;
  } else {
    score -= sourceState.missingRequiredKinds.length * 6;
    notes.push(
      `missing sources: ${sourceState.missingRequiredKinds.join(', ')}`,
    );
  }

  return {
    template,
    sourceState,
    trace: {
      candidateId: template.id,
      score,
      droppedReasons,
      notes,
      selected: false,
      fallbackApplied: false,
    },
  };
}

function resolveTemplateCandidate(
  sectionType: string,
  required: boolean,
  family: FamilyRegistry,
  theme: ThemeRegistry,
  input: BuildPageCompositionInput,
) {
  const evaluated = listSectionTemplatesByType(sectionType as never).map((template) =>
    evaluateTemplateCandidate(template, family, theme, input),
  );

  const viable = evaluated.filter(
    (candidate) => candidate.trace.droppedReasons.length === 0,
  );

  if (viable.length === 0) {
    return {
      selected: null,
      fallbackApplied: false,
      trace: evaluated.map((candidate) => candidate.trace),
      omitted: !required,
      warning: `No template available for ${sectionType}`,
    };
  }

  const byScore = [...viable].sort((left, right) => right.trace.score - left.trace.score);
  let selected =
    byScore.find(
      (candidate) => candidate.sourceState.missingRequiredKinds.length === 0,
    ) ?? byScore[0];
  let fallbackApplied = false;

  if (
    selected.sourceState.missingRequiredKinds.length > 0 &&
    selected.template.emptyStateRules.policy === 'fallback_template' &&
    selected.template.emptyStateRules.fallbackTemplateId
  ) {
    const fallbackTemplate = getSectionTemplateRegistry(
      selected.template.emptyStateRules.fallbackTemplateId,
    );

    if (fallbackTemplate) {
      const fallbackCandidate = evaluateTemplateCandidate(
        fallbackTemplate,
        family,
        theme,
        input,
      );

      if (fallbackCandidate.trace.droppedReasons.length === 0) {
        selected = fallbackCandidate;
        fallbackApplied = true;
      }
    }
  }

  if (
    !required &&
    selected.sourceState.missingRequiredKinds.length > 0 &&
    selected.template.emptyStateRules.policy === 'omit_section'
  ) {
    return {
      selected: null,
      fallbackApplied: false,
      trace: evaluated.map((candidate) => candidate.trace),
      omitted: true,
      warning: `${sectionType} omitted due to missing sources`,
    };
  }

  selected.trace.selected = true;
  selected.trace.fallbackApplied = fallbackApplied;

  return {
    selected,
    fallbackApplied,
    trace: evaluated.map((candidate) =>
      candidate.template.id === selected.template.id
        ? selected.trace
        : candidate.trace,
    ),
    omitted: false,
    warning:
      fallbackApplied || selected.sourceState.missingRequiredKinds.length > 0
        ? `${sectionType} used fallback or is waiting for source completion`
        : undefined,
  };
}

function buildCompositionFieldSpec(
  field: SectionTemplateFieldSpec,
  template: SectionTemplateRegistry,
) : CompositionFieldSpec {
  const generationMode: CompositionFieldSpec['generationMode'] = !field.allowAiGeneration
    ? 'system_locked'
    : field.kind === 'text' || field.kind === 'textarea'
      ? 'hybrid_refine'
      : 'ai_generated';
  const sourceRequirement: CompositionFieldSpec['sourceRequirement'] = field.requiresVerifiedSource
    ? 'verified'
    : field.sourceKinds.length > 0
      ? 'optional'
      : 'none';

  return {
    name: field.name,
    kind: field.kind,
    required: field.required,
    generationMode,
    sourceRequirement,
    minItems: field.minItems,
    maxItems: field.maxItems,
    itemShape: field.itemShape,
    copyConstraint:
      field.allowAiGeneration && (field.kind === 'text' || field.kind === 'textarea')
        ? {
            maxHeadingLength:
              field.name.includes('headline') || field.name.includes('heading')
                ? template.contentRules.maxHeadingLength
                : undefined,
            maxBodyLength:
              field.kind === 'textarea'
                ? template.contentRules.maxBodyLength
                : undefined,
            tone: template.contentRules.toneNotes.join(' / ') || undefined,
            prohibitedPatterns: template.contentRules.prohibitedPatterns,
          }
        : undefined,
  };
}

function buildRenderHints(
  sectionType: string,
  template: SectionTemplateRegistry,
  dominantChannel: ThemeEmphasisChannel,
  ctaIntensity: CtaEmphasisLevel,
  theme: ThemeRegistry,
  order: number,
) {
  const isHighEmphasis =
    sectionType === 'hero' ||
    (sectionType === 'proof' && dominantChannel === 'proof') ||
    (sectionType === 'cta' &&
      (dominantChannel === 'cta' || ctaIntensity === 'strong')) ||
    (sectionType === 'benefit' && dominantChannel === 'visual');

  const backgroundVariant =
    ['proof', 'review', 'faq', 'pricing', 'form'].includes(sectionType)
      ? theme.componentRules.backgroundTreatment === 'subtle_surface'
        ? 'surface'
        : 'muted'
      : sectionType === 'cta'
        ? 'muted'
        : 'default';

  const mobileBehavior =
    template.id === 'cta_sticky_mobile'
      ? 'sticky_cta'
      : template.layoutType === 'carousel'
        ? 'carousel'
        : sectionType === 'faq'
          ? 'accordion'
          : 'stack';

  return {
    emphasis: isHighEmphasis ? 'high' : sectionType === 'cta' ? 'normal' : 'low',
    backgroundVariant,
    mobileBehavior,
    anchorId: `${sectionType}-${order}`,
  } as const;
}

function buildSectionCopyGoal(
  sectionType: string,
  template: SectionTemplateRegistry,
  family: FamilyRegistry,
  objective?: BuildPageCompositionInput['classification']['objective']['value'],
  theme?: ThemeRegistry,
) {
  switch (template.id) {
    case 'hero_b2b_centered':
      if (family.id === 'btob_inquiry' && objective === 'document_request') {
        return '比較検討中の法人に向けて、この資料で何が分かるのか、どんな判断がしやすくなるのか、どんな実績があるのかを一画面で伝えるHeroにする。Sansanや楽楽精算のような、誠実で情報整理された日本のBtoB資料請求LPを意識する。';
      }
      if (family.id === 'regional_service_inquiry') {
        return '地域サービスを探している読み手に向けて、対応内容・受付体制・無料見積・保証などの安心材料を冒頭で整理し、問い合わせ前の不安を下げるHeroにする。水道修理や鍵交換のLPのように、すぐ相談できる信頼感を一画面で伝える。';
      }
      return '比較検討中の法人に向けて、対象読者・得られる成果・安心材料を一読で伝えるHeroにする。誠実で具体的な日本のBtoB LPの冒頭を意識する。';
    case 'hero_saas_split_visual':
      if (family.id === 'btob_inquiry' && objective === 'document_request') {
        return '比較検討中の法人担当者に向けて、管理画面や導入イメージを見せながら、この資料で分かる範囲と導入後の変化を一画面で理解させるHeroにする。UI理解から資料請求へ自然につなげる。';
      }
      if (family.id === 'saas_ai_inquiry') {
        return 'SaaSやAIツールを比較検討する担当者に向けて、主要画面で何ができるのか、どの業務がどう楽になるのか、デモで何を確認すべきかを一画面で理解させるHeroにする。日本のSaaS問い合わせLPのように、UI理解からデモ相談へ自然につなげる。';
      }
      return '担当者の業務課題、導入後に何が改善されるか、UIで確認できる価値を一画面で理解させるHeroにする。デモ相談したくなる温度感にする。';
    case 'hero_store_local_visual':
      if (family.id === 'regional_service_inquiry' && theme?.id === 'theme_natural_warm') {
        return '地域の事情や家族の気持ちに寄り添いながら、内容が未整理でも相談できること、まずは話だけでもよいこと、訪問や見積の前に不安を整理できることをやわらかく伝えるHeroにする。遺品整理や生活支援系の地域サービスLPのように、押し売り感なく相談しやすい空気を作る。';
      }
      return '地域性、通いやすさ、相談しやすさ、予約後のイメージを短く伝えるHeroにする。店舗や地域サービスの日本LPのように安心感と行きやすさを両立させる。';
    case 'hero_product_focus':
      return '商品カテゴリー、悩み、使用後ベネフィット、初回購入や限定オファーを短くまとめて、購入CTAに自然につなぐHeroにする。';
    case 'hero_people_split':
      return family.id === 'recruitment_entry'
        ? '誰と働くのか、どんなミッションに挑むのか、応募する価値は何かを一画面で伝える採用Heroにする。'
        : '誰が提供するのか、どんな想いと専門性があるのか、参加・申込する価値は何かを一画面で伝えるHeroにする。';
    case 'hero_problem_first':
      if (family.id === 'regional_service_inquiry') {
        return '読み手が今まさに困っている症状や不安を先に言語化しつつ、すぐ相談できること、作業前に費用や流れを確認できることまで短く伝えるHeroにする。緊急性の高い地域サービスLPのように、焦らせすぎず安心して問い合わせできる温度感にする。';
      }
      return family.id === 'btob_inquiry' && objective === 'document_request'
        ? '読み手が抱える業務課題を先に言語化しつつ、課題をあおるだけで終わらず、この資料で分かる解決の方向性と比較検討のしやすさまでつなぐHeroにする。'
        : '読み手が抱える悩みを先に言語化し、その直後に解決の方向性と次の行動を示すHeroにする。課題共感から相談理由までを短くつなぐ。';
    case 'hero_empathy_story':
      if (family.id === 'regional_service_inquiry' && theme?.id === 'theme_natural_warm') {
        return '読み手の迷いや負担感に寄り添い、内容が未整理でも相談できること、無理に決めなくても大丈夫なことをやわらかく伝えるHeroにする。遺品整理や生活支援の相談LPのように、気持ちを軽くする導入を意識する。';
      }
      return '不安や迷いに寄り添い、利用後の前向きな変化を静かに想像させるHeroにする。感情だけで終わらず、行動理由までつなげる。';
    case 'problem_b2b_structured':
      return '現場担当者や決裁者が抱えやすい業務課題を、社内で共有しやすい言葉で3〜4点に整理するproblem sectionにする。抽象論ではなく、比較検討の出発点になる困りごとに絞る。';
    case 'solution_visual_diagram':
      return '課題に対してどのように整理・改善・運用できるのかを、資料を読むだけで流れがつかめるsolution sectionにする。導入フローではなく解決の考え方を理解させる。';
    case 'proof_metric_cards':
      if (family.id === 'btob_inquiry' && objective === 'document_request') {
        return '導入社数、改善実績、対応スピード、継続率などのverified dataを一目で理解できるように並べ、資料請求前の不安を下げるproof sectionにする。資料DLの理由になる数字だけを厳選する。';
      }
      if (family.id === 'saas_ai_inquiry') {
        return '導入社数、継続利用率、削減時間、活用部署数などのverified dataを一目で理解できるように並べ、デモ相談前の不安を下げるproof sectionにする。SaaSの比較検討で効く数字だけを短く見せる。';
      }
      if (family.id === 'regional_service_inquiry') {
        return '対応件数、到着スピード、受付時間、保証年数などのverified dataを一目で理解できるように並べ、問い合わせ前の不安を下げるproof sectionにする。緊急性の高い地域サービスLPで効く数字だけを厳選する。';
      }
      return '導入社数、改善実績、満足度などのverified dataを一目で理解できるように並べ、不安を下げるproof sectionにする。数字の意味がすぐ伝わることを優先する。';
    case 'proof_badges_awards':
      return family.id === 'btob_inquiry' && objective === 'document_request'
        ? '認証、導入業種、支援体制などの補助的な信頼材料を短く並べ、数字実績の補強として素早く読めるproof sectionにする。'
        : family.id === 'regional_service_inquiry' && theme?.id === 'theme_natural_warm'
          ? '資格、許可、地域密着の姿勢、相談体制などの安心材料を短く並べ、やさしく相談できる根拠として読めるproof sectionにする。数字を主役にしすぎず、依頼前の不安がほどける並びにする。'
        : family.id === 'regional_service_inquiry'
          ? '資格、指定工事店、保証、対応メーカー数などの補助的な信頼材料を短く並べ、地域サービスとして安心して問い合わせできる根拠を素早く読めるproof sectionにする。'
        : '資格、受賞、導入実績などを短いラベルで並べ、実在性と安心材料として素早く読めるproof sectionにする。';
    case 'review_case_reinforcement':
      return family.id === 'btob_inquiry' && objective === 'document_request'
        ? '比較検討中の法人が社内共有しやすいように、導入背景・利用後の変化・評価理由が短く整理された事例補強型のreview sectionにする。感想ではなく判断材料として読める具体性を優先する。'
        : '導入背景・利用後の変化・評価理由が短く整理された事例補強型のreview sectionにする。';
    case 'review_speech_bubbles':
      return family.id === 'regional_service_inquiry' && theme?.id === 'theme_natural_warm'
        ? '同じ悩みを持つ読み手が安心して相談できそうだと思えるように、迷っていた点、相談してみてほっとした点、依頼後の気持ちの変化が短く伝わるreview sectionにする。強い絶賛よりも、相談しやすさが伝わる具体性を優先する。'
        : family.id === 'regional_service_inquiry'
        ? '同じ悩みを持つ読み手が自分ごと化しやすいように、依頼内容・地域感・対応後の安心感が短く伝わるreview sectionにする。褒め言葉だけでなく、相談してよかった理由が見える具体性を優先する。'
        : '同じ悩みを持つ読み手が自分ごと化しやすいように、悩み・利用後の変化・満足理由が短く伝わるreview sectionにする。';
    case 'review_with_photo':
      return family.id === 'regional_service_inquiry' && theme?.id === 'theme_natural_warm'
        ? '写真や属性がある場合は相談者の安心感を高めつつ、依頼前の迷いと相談後の気持ちの変化が具体的に浮かぶreview sectionにする。写真だけに頼らず、相談しやすさが伝わるコメントを優先する。'
        : '写真や属性とセットでレビューの実在感を高め、利用後の変化が具体的に浮かぶreview sectionにする。写真頼みではなくコメントの具体性を重視する。';
    case 'review_carousel':
      return 'スマホで流し読みしてもベネフィットが伝わるよう、1枚1主張で軽く読めるreview sectionにする。似た内容を重ねず、違う安心材料が見えるようにする。';
    case 'faq_b2b_reassurance_panels':
      return family.id === 'btob_inquiry' && objective === 'document_request'
        ? '資料請求前によく出る不安を、比較検討や社内共有の文脈で判断しやすい短い回答に整理したfaq sectionにする。送信後の流れや営業色の弱さも自然に伝える。'
        : '問い合わせや申込前によく出る不安を、判断しやすい短い回答に整理したfaq sectionにする。';
    case 'cta_simple_center':
      return family.id === 'btob_inquiry' && objective === 'document_request'
        ? 'ここで資料請求する理由、送信後の流れ、押した後に何が起こるか、資料を受け取った後に何が分かるか、比較検討や社内共有にどう役立つかを短く示し、安心して押せるCTA sectionにする。'
        : family.id === 'saas_ai_inquiry'
          ? 'ここでデモ相談や問い合わせを送る理由、送信後に何が起こるか、どの画面や運用イメージを確認できるかを短く示し、安心して押せるCTA sectionにする。営業色を上げすぎず、比較検討の次の一歩として自然に見せる。'
          : family.id === 'regional_service_inquiry' && theme?.id === 'theme_natural_warm'
            ? 'ここで無料相談や見積依頼を送る理由、まだ内容が決まっていなくても大丈夫なこと、送信後にどのように案内されるかを短く示し、安心して相談を始められるCTA sectionにする。やわらかく背中を押しつつ、急かしすぎない温度感にする。'
          : family.id === 'regional_service_inquiry'
            ? 'ここで無料相談や見積依頼を送る理由、送信後の流れ、作業前にどこまで確認できるかを短く示し、安心して問い合わせできるCTA sectionにする。押し売り感ではなく、相談しやすさが伝わる温度感にする。'
        : 'ここで行動する理由、押した後に何が起こるか、誰に向いているCTAかを短く示し、安心して押せるCTA sectionにする。';
    case 'cta_b2b_reassurance_panel':
      return family.id === 'btob_inquiry' && objective === 'document_request'
        ? '資料請求前の最終後押しとして、資料で確認できること、送信後の流れ、比較検討や社内共有に役立つ点を短く整理し、安心して押せるCTA sectionにする。'
        : family.id === 'saas_ai_inquiry'
          ? 'デモ相談前の最終後押しとして、デモで確認できること、送信後の流れ、運用や連携の相談につながる点を短く整理し、安心して押せるCTA sectionにする。'
        : '行動前の不安を下げつつ、押した後の流れまで短く示すCTA sectionにする。';
    case 'cta_banner_emphasis':
      return '今動く理由を一つに絞って強く後押しするCTAにする。限定性や申込理由は明快にしつつ、安っぽい煽りにはしない。';
    case 'services_menu_blocks':
      return family.id === 'btob_inquiry' && objective === 'document_request'
        ? '支援範囲や提供内容を、比較検討中の法人が自社に必要な範囲を判断できる粒度で整理するservices sectionにする。抽象的な価値訴求ではなく、何を任せられるかが分かる並びにする。'
        : family.id === 'regional_service_inquiry' && theme?.id === 'theme_natural_warm'
          ? '遺品整理、空き家片付け、生前整理、立ち会いなし相談など、何をどこまで相談できるかがやさしく伝わるservices sectionにする。まだ依頼内容が固まりきっていない読み手でも、自分が相談してよい範囲を判断できる粒度にする。'
        : family.id === 'regional_service_inquiry'
          ? 'トラブルの種類や依頼内容ごとに、何をどこまで頼めるかが一目で分かるservices sectionにする。緊急性の高い読み手でも自分に必要な対応内容をすぐ判断できる粒度で整理する。'
        : '相談・施術・支援範囲など、提供メニューを具体的に把握できるservices sectionにする。読み手が自分に必要な内容を判断できる粒度で整理する。';
    case 'reasons_icon_list':
      return family.id === 'regional_service_inquiry' && theme?.id === 'theme_natural_warm'
        ? '無理な営業をしない、話だけでも相談できる、地域事情に詳しい、立ち会いが難しいケースにも配慮できるなど、安心して相談できる理由を3〜5点でやわらかく整理するreasons sectionにする。権威よりも寄り添い方が伝わる内容を優先する。'
        : family.id === 'regional_service_inquiry'
        ? '見積無料、即日対応、保証、地域密着、追加費用の分かりやすさなど、問い合わせ前に知りたい安心材料を3〜5点で端的に整理するreasons sectionにする。proof の数字を繰り返さず、依頼先として選ばれる理由を短く見せる。'
        : '選ばれる理由を3〜5点で端的に整理し、数字実績とは別の判断材料として読みやすく見せるreasons sectionにする。';
    case 'features_visual_explain':
      return family.id === 'btob_inquiry' && objective === 'document_request'
        ? '比較検討中の法人が、他社との違いや選定ポイントを理解できるfeatures sectionにする。UIや運用イメージと結びつけて、なぜ選ばれるのかを差分で見せる。'
        : family.id === 'saas_ai_inquiry'
          ? '主要機能がどの業務の流れに効くのか、どの画面で何を確認できるのかをつなげて理解できるfeatures sectionにする。機能一覧ではなく、運用イメージが浮かぶ説明にする。'
        : '他社と比べた強みや選ぶ理由を理解できるfeatures sectionにする。何を提供するかではなく、なぜ選ばれるのかを差分として見せる。';
    case 'feature_list_compact':
      return family.id === 'btob_inquiry' && objective === 'document_request'
        ? '主要な機能や特徴を一覧で素早く把握できるfeature listにする。比較検討の初期段階でも違いが分かるよう、1項目ごとに別の価値や判断軸が見える並びにする。'
        : family.id === 'saas_ai_inquiry'
          ? '主要な機能や使いどころを一覧で素早く把握できるfeature listにする。デモ前でも全体像がつかみやすく、どの機能を確認したいかが整理できる並びにする。'
        : '主要な機能や特徴を一覧で素早く把握できるfeature listにする。1項目ごとに違う価値が見え、比較検討に使いやすい並びにする。';
    case 'comparison_table':
      return family.id === 'saas_ai_inquiry'
        ? '比較検討中の担当者が、デモで確認したい軸や運用上の違いを見比べられるcomparison sectionにする。機能名の列挙ではなく、連携・使い勝手・導入しやすさなど判断材料になる比較ポイントに絞る。'
        : '比較検討中の法人が、社内共有しやすい軸で違いを見比べられるcomparison sectionにする。機能名の列挙ではなく、判断材料になる比較ポイントに絞る。';
    case 'pricing_compact_cards':
      return '料金やプラン差分を短時間で判断できるpricing sectionにする。価格だけでなく、何が含まれるかと選び分けの基準が分かるようにする。';
    case 'pricing_consultation':
      return '価格の個別性が高い理由と、相談するメリットが納得感を持って伝わるpricing sectionにする。不透明さではなく個別最適であることを示す。';
    case 'flow_standard':
      return family.id === 'regional_service_inquiry'
        ? '問い合わせから状況確認、訪問または見積、作業完了までの流れを3〜5ステップで簡潔に示し、依頼の気軽さと分かりやすさを伝えるflow sectionにする。緊急時でも進め方が想像できる並びにする。'
        : '利用開始までの流れを3〜5ステップで簡潔に示し、次の行動に進みやすくするflow sectionにする。';
    case 'faq_cards':
      return family.id === 'regional_service_inquiry' && theme?.id === 'theme_natural_warm'
        ? '「まずは話だけ聞きたい」「何を伝えればいいかわからない」「遠方でも相談できるか」など、相談前に出やすい不安を短くやさしく返すfaq sectionにする。問い合わせのハードルを下げることを優先する。'
        : '問い合わせや申込の前によく出る不安を、軽やかに読み進められるfaq sectionにする。';
    case 'access_simple_text':
      return family.id === 'regional_service_inquiry' && theme?.id === 'theme_natural_warm'
        ? '所在地や対応エリア、訪問相談の可否などを簡潔に示し、地元で相談しやすい安心感を作るaccess sectionにする。来店予約の情報ではなく、地域で頼れる感覚が伝わる内容を優先する。'
        : '住所や最寄り情報など、次の行動に必要なアクセス情報を簡潔に示すsectionにする。';
    case 'message_text_block':
      return family.id === 'regional_service_inquiry' && theme?.id === 'theme_natural_warm'
        ? '「一人で抱え込まなくてよい」「内容が決まりきっていなくても大丈夫」といった姿勢を、短くまっすぐ伝えるmessage sectionにする。代表挨拶のように長くせず、相談への心理的ハードルを下げる一言に絞る。'
        : '想いや姿勢を短くまっすぐ伝え、他の説明セクションでは補いにくい温度感を支えるmessage sectionにする。';
    case 'form_standard_contact':
      return family.id === 'btob_inquiry' && objective === 'document_request'
        ? '資料請求フォームの直前説明として、送信後に何が届くか、どんな比較検討に役立つか、営業色が強すぎない安心感まで伝えるform sectionにする。'
        : family.id === 'saas_ai_inquiry'
          ? '問い合わせフォームの直前説明として、デモ相談で何を確認できるか、送信後にどう案内されるか、運用や連携の相談もできる安心感まで伝えるform sectionにする。'
          : family.id === 'regional_service_inquiry' && theme?.id === 'theme_natural_warm'
            ? '問い合わせフォームの直前説明として、話だけでもよいこと、内容が固まっていなくても大丈夫なこと、送信後はやさしく状況確認から始まることまで伝えるform sectionにする。入力前の気持ちを軽くする。'
          : family.id === 'regional_service_inquiry'
            ? '問い合わせフォームの直前説明として、相談だけでもよいこと、見積無料であること、送信後にどのように案内されるかまで伝えるform sectionにする。入力前の不安を減らし、安心して送信できる状態を作る。'
        : '入力前の不安を減らし、送信後の流れが想像できるform sectionにする。';
    default:
      return template.description;
  }
}

const TONE_TO_WRITING_STYLE: Record<VisualTone, WritingStyle> = {
  trust: 'formal_desu_masu',
  corporate: 'formal_desu_masu',
  friendly: 'soft_desu_masu',
  premium: 'premium_terse',
  modern: 'modern_concise',
  bright_clean: 'soft_desu_masu',
  natural_warm: 'warm_conversational',
};

const APPEAL_STYLE_TO_HERO_PATTERN: Record<AppealStyle, HeroPattern> = {
  problem_solution: 'problem_first',
  benefit_future: 'benefit_first',
  feature_understanding: 'feature_showcase',
  trust_relief: 'trust_authority',
  story_empathy: 'empathy_story',
  offer_push: 'offer_urgency',
  people_culture: 'culture_people',
  authority_expert: 'trust_authority',
};

export function selectHeroPattern(
  classification: BuildPageCompositionInput['classification'],
): HeroPattern {
  return APPEAL_STYLE_TO_HERO_PATTERN[classification.appealStyle.primary.value];
}

export function toneToWritingStyle(tone: VisualTone): WritingStyle {
  return TONE_TO_WRITING_STYLE[tone];
}

export function buildPageContext(
  input: BuildPageCompositionInput,
  skeleton: SkeletonRegistry,
  theme: ThemeRegistry,
  selectedSections: string[],
): PageContext {
  const tone = theme.visualTone;
  const writingStyle = toneToWritingStyle(tone);

  const emotionalArc = skeleton.emotionalArc ?? '';

  const sectionRoles = selectedSections.map((sectionType, index) => {
    const emotionalRole = extractEmotionalRole(sectionType, emotionalArc);
    const prevSection = index > 0 ? selectedSections[index - 1] : undefined;
    return {
      sectionType: sectionType as PageContext['sectionRoles'][number]['sectionType'],
      emotionalRole,
      transitionFrom: prevSection
        ? `${prevSection}からの流れ`
        : undefined,
    };
  });

  const ctaPlacements = skeleton.ctaPlacements ?? [];

  return {
    emotionalArc,
    copyDirection: {
      tone,
      writingStyle,
      targetPersona: buildTargetPersona(input),
      keyMessage: buildKeyMessage(input),
      differentiator: buildDifferentiator(input),
    },
    sectionRoles,
    ctaStrategy: {
      primaryAction: derivePrimaryAction(input.classification.objective.value),
      ctaPlacements,
    },
  };
}

function extractEmotionalRole(sectionType: string, emotionalArc: string): string {
  const pattern = new RegExp(`${sectionType}（([^）]+)）`);
  const match = emotionalArc.match(pattern);
  return match ? match[1] : sectionType;
}

function buildTargetPersona(input: BuildPageCompositionInput): string {
  if (input.normalizedHints?.audience) {
    return input.normalizedHints.audience;
  }
  const industry = input.classification.industry.value;
  const objective = input.classification.objective.value;
  return `${industry} / ${objective} 向けターゲット`;
}

function buildKeyMessage(input: BuildPageCompositionInput): string {
  if (input.normalizedHints?.serviceSummary) {
    return input.normalizedHints.serviceSummary;
  }
  const evidence = input.classification.appealStyle.primary.evidence ?? [];
  return evidence.length > 0 ? evidence[0] : input.classification.appealStyle.primary.value;
}

function buildDifferentiator(input: BuildPageCompositionInput): string {
  if (input.normalizedHints?.keyStrengths && input.normalizedHints.keyStrengths.length > 0) {
    return input.normalizedHints.keyStrengths.join('、');
  }
  const secondary = input.classification.appealStyle.secondary ?? [];
  if (secondary.length > 0) {
    return secondary.join(' + ');
  }
  return input.classification.appealStyle.primary.value;
}

function derivePrimaryAction(objective: string): string {
  const map: Record<string, string> = {
    inquiry: '問い合わせ',
    document_request: '資料請求',
    reservation: '予約',
    application: '申し込み',
    purchase: '購入',
    recruitment: '応募',
  };
  return map[objective] ?? '問い合わせ';
}

type CompositionSectionMutable = ReturnType<typeof compositionSectionSchema.parse>;

function insertSubCtaSections(
  sections: CompositionSectionMutable[],
  skeleton: SkeletonRegistry,
  family: FamilyRegistry,
  theme: ThemeRegistry,
  input: BuildPageCompositionInput,
  warnings: string[],
): void {
  const placements = skeleton.ctaPlacements ?? [];
  if (placements.length === 0 || !skeleton.ctaPolicy.allowInlineSubCta) return;

  const midPlacements = placements.filter(
    (p) => p.afterSectionType && p.intensity !== 'strong',
  );
  if (midPlacements.length === 0) return;

  const ctaResolved = resolveTemplateCandidate('cta', false, family, theme, input);
  if (!ctaResolved.selected) {
    warnings.push('sub_cta: no viable CTA template found, skipping inline CTA insertion');
    return;
  }

  const subCtaTemplate = ctaResolved.selected.template;
  let inserted = 0;

  for (const placement of midPlacements) {
    const afterIndex = sections.findIndex(
      (s) => s.sectionType === placement.afterSectionType,
    );
    if (afterIndex < 0) continue;

    const insertAt = afterIndex + 1 + inserted;
    const nextSection = sections[insertAt];
    if (nextSection?.sectionType === 'cta' || nextSection?.sectionType === 'sub_cta') {
      continue;
    }

    const subCtaRuntime = buildSectionRuntimeState(subCtaTemplate, input.sourceInventory);
    const systemFieldValues: SectionSystemFieldValues = {
      ...subCtaRuntime.systemFieldValues,
    };
    if (placement.suggestedLabel) {
      systemFieldValues['primaryCtaLabel'] = placement.suggestedLabel;
    }

    sections.splice(insertAt, 0, {
      id: `sub_cta-after-${placement.afterSectionType}`,
      order: 0,
      sectionType: 'sub_cta' as typeof sections[number]['sectionType'],
      templateId: subCtaTemplate.id,
      required: false,
      generationStatus: 'ready' as const,
      sourcePolicy: subCtaRuntime.sourcePolicy,
      sourceBindings: subCtaRuntime.sourceBindings,
      contentSpec: {
        requiredFields: subCtaTemplate.requiredFields.map((field) =>
          buildCompositionFieldSpec(field, subCtaTemplate),
        ),
        optionalFields: [],
        copyGoal: `${placement.position}のCTA（${placement.intensity}）`,
        contentRules: [`CTA強度: ${placement.intensity}`],
      },
      assetSpec: {
        requiredAssetKinds: [],
        optionalAssetKinds: [],
        emptyStatePolicy: 'omit_optional_fields' as const,
      },
      visualSlots: buildSectionVisualSlots(subCtaTemplate),
      systemFieldValues,
      renderHints: {
        emphasis: placement.intensity === 'normal' ? 'normal' as const : 'low' as const,
        backgroundVariant: 'muted' as const,
        mobileBehavior: 'stack' as const,
      },
    });
    inserted++;
  }

  if (inserted > 0) {
    for (let i = 0; i < sections.length; i++) {
      sections[i].order = i + 1;
    }
  }
}

export function buildPageComposition(
  rawInput: BuildPageCompositionInput,
): PageCompositionJson {
  const input = buildPageCompositionInputSchema.parse(rawInput);
  const familySelection = selectFamily(input);
  const skeletonSelection = selectSkeleton(familySelection.selected, input);
  const themeSelection = selectTheme(familySelection.selected, input);
  const selectedThemeSnapshot: SelectedThemeSnapshot = buildSelectedThemeSnapshot(
    themeSelection.selected,
  );
  const dominantChannel = themeSelection.selected.emphasisPriority[0];
  const baseCtaIntensity = desiredCtaIntensity(input.classification.objective.value);
  const cappedCtaIntensity = capCtaIntensity(
    baseCtaIntensity,
    themeSelection.selected.ctaHarmony.maxPrimaryEmphasis,
  );
  const selectedSectionTypes = selectSectionTypes(
    familySelection.selected,
    skeletonSelection.selected,
    themeSelection.selected,
    input,
  );

  const warnings: string[] = [];
  const blockedByMissingSources: string[] = [];
  const nonBlockingSourceWaits: string[] = [];
  const sectionsTrace: SelectionTrace['sections'] = [];

  const sections = selectedSectionTypes.flatMap((sectionType, index) => {
    const required = skeletonSelection.selected.requiredSections.includes(
      sectionType as never,
    );
    const resolved = resolveTemplateCandidate(
      sectionType,
      required,
      familySelection.selected,
      themeSelection.selected,
      input,
    );

    if (resolved.warning) {
      warnings.push(resolved.warning);
    }

    if (resolved.selected === null) {
      if (required) {
        blockedByMissingSources.push(`${sectionType}: template unavailable`);
      }
      return [];
    }

    sectionsTrace.push({
      sectionType: sectionType as never,
      templateCandidates: resolved.trace,
      selectedTemplateId: resolved.selected.template.id,
      fallbackApplied: resolved.fallbackApplied,
    });

    const template = resolved.selected.template;
    const runtimeState = buildSectionRuntimeState(
      template,
      input.sourceInventory,
    );
    const sourceState = runtimeState.sourceBindings;
    const generationStatus = runtimeState.generationStatus;

    if (required && generationStatus !== 'ready') {
      const reason = `${sectionType}: ${sourceState.missingRequiredKinds.join(', ')}`;
      if (
        generationStatus === 'needs_verified_source' &&
        ['proof', 'review', 'pricing', 'access'].includes(sectionType)
      ) {
        nonBlockingSourceWaits.push(reason);
      } else {
        blockedByMissingSources.push(reason);
      }
    }

    return [
      {
        id: `${sectionType}-${index + 1}`,
        order: index + 1,
        sectionType,
        templateId: template.id,
        required,
        generationStatus,
        sourcePolicy: runtimeState.sourcePolicy,
        sourceBindings: runtimeState.sourceBindings,
        contentSpec: {
          requiredFields: template.requiredFields.map((field) =>
            buildCompositionFieldSpec(field, template),
          ),
          optionalFields: template.optionalFields.map((field) =>
            buildCompositionFieldSpec(field, template),
          ),
          copyGoal: buildSectionCopyGoal(
            sectionType,
            template,
            familySelection.selected,
            input.classification.objective.value,
            themeSelection.selected,
          ),
          contentRules: [
            ...template.contentRules.toneNotes,
            ...template.contentRules.prohibitedPatterns.map(
              (pattern) => `禁止: ${pattern}`,
            ),
          ],
        },
        assetSpec: {
          requiredAssetKinds: template.assetRequirements
            .filter((asset) => asset.required)
            .map((asset) => asset.assetKind),
          optionalAssetKinds: template.assetRequirements
            .filter((asset) => !asset.required)
            .map((asset) => asset.assetKind),
          emptyStatePolicy: template.emptyStateRules.policy,
          fallbackTemplateId: template.emptyStateRules.fallbackTemplateId,
          blockingReason:
            sourceState.missingRequiredKinds.length > 0
              ? `Missing sources: ${sourceState.missingRequiredKinds.join(', ')}`
              : undefined,
        },
        visualSlots: buildSectionVisualSlots(template),
        systemFieldValues: runtimeState.systemFieldValues,
        renderHints: buildRenderHints(
          sectionType,
          template,
          dominantChannel,
          cappedCtaIntensity,
          themeSelection.selected,
          index + 1,
        ),
      },
    ];
  });

  insertSubCtaSections(
    sections,
    skeletonSelection.selected,
    familySelection.selected,
    themeSelection.selected,
    input,
    warnings,
  );

  const highEmphasisSectionIds = sections
    .filter((section) => section.renderHints.emphasis === 'high')
    .map((section) => section.id);

  const pageDesignGuard = {
    dominantChannel,
    secondaryChannels: themeSelection.selected.emphasisPriority.slice(1, 3),
    ctaIntensity: cappedCtaIntensity,
    highEmphasisSectionIds,
    notes:
      cappedCtaIntensity !== baseCtaIntensity
        ? ['CTA intensity was capped by theme harmony']
        : [],
  };

  const errors: string[] = [];
  for (const requiredSection of skeletonSelection.selected.requiredSections) {
    if (!sections.some((section) => section.sectionType === requiredSection)) {
      errors.push(`required section ${requiredSection} is missing`);
    }
  }

  for (const section of sections) {
    if (
      selectedThemeSnapshot.forbiddenSectionTemplateIds.includes(section.templateId)
    ) {
      errors.push(`template ${section.templateId} is forbidden by theme`);
    }
  }

  if (
    input.classification.visualTone.value !== themeSelection.selected.visualTone
  ) {
    warnings.push('selected theme uses a safe fallback visual tone');
  }

  if (
    themeSelection.selected.materialSensitivity === 'high' &&
    input.sourceInventory.summary.overallMaterialQuality === 'low'
  ) {
    warnings.push('high-sensitivity theme selected with low material quality');
  }

  if (nonBlockingSourceWaits.length > 0) {
    warnings.push(
      'some sections are waiting for verified sources and will be omitted from the first draft',
    );
  }

  const selectionTrace: SelectionTrace = {
    family: familySelection.trace,
    skeleton: skeletonSelection.trace,
    theme: themeSelection.trace,
    sections: sectionsTrace,
  };

  const pageContext = buildPageContext(
    input,
    skeletonSelection.selected,
    themeSelection.selected,
    selectedSectionTypes,
  );

  return pageCompositionJsonSchema.parse({
    version: 1,
    siteName: input.siteName,
    companyName: input.companyName,
    classification: input.classification,
    sourceInventory: input.sourceInventory,
    selectedFamilyId: familySelection.selected.id,
    selectedSkeletonId: skeletonSelection.selected.id,
    selectedThemeId: themeSelection.selected.id,
    selectedThemeSnapshot,
    selectionTrace,
    pages: [
      {
        pageId: 'home',
        pageRole: 'landing',
        title: 'トップ',
        path: '/',
        pageDesignGuard,
        sections,
      },
    ],
    validation: {
      // 最低限 hero (or その他の最初の見せセクション) と CTA が組めていれば
      // 保存可能とみなす。組み立てきれなかった必須セクションは warning として残し、
      // ユーザが後からエディタで追加できるようにする。
      isValid:
        (() => {
          if (sections.length === 0) return false;
          const hasHero = sections.some((section) => section.sectionType === 'hero');
          const hasCta = sections.some(
            (section) => section.sectionType === 'cta' || section.sectionType === 'form',
          );
          return hasHero && hasCta;
        })(),
      errors,
      warnings,
      blockedByMissingSources: unique([
        ...blockedByMissingSources,
        ...nonBlockingSourceWaits,
      ]),
    },
    pageContext,
  });
}
